// Icons and Images
import CircularProgress from '@mui/material/CircularProgress'
import { NotificationUI } from 'features/notifications'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { appSelectors } from 'redux/reducers/app'
import { appConnector } from 'shared/connector'
import { notifyError } from 'shared/errors'
import { useAppSelector } from 'shared/hooks'

// Components
import { BadAuthContainer } from '../containers/badAuthContainer'

const enum Status {
	Loading,
	Error,
	Success
}

export function AuthCallback() {
	const [status, setStatus] = useState(Status.Loading)

	const isReadyConnector = useAppSelector(appSelectors.isReadyConnector)

	const { search } = useLocation()
	const { push } = useHistory()

	const showError = useCallback((error: unknown) => {
		notifyError(error)
		setStatus(Status.Error)
	}, [])

	useEffect(() => {
		if (!isReadyConnector) {
			return
		}

		const parsed = new URLSearchParams(search)

		const error = parsed.get('error')

		if (error) {
			return showError(error)
		}

		const code = parsed.get('code')

		if (code) {
			const redirect = parsed.get('state') ?? '/'
			processCode(code)
				.then(() => push(redirect))
				.catch(showError)

			return
		}
	}, [isReadyConnector, search, showError, push])

	return (
		<div className='auth-call-back-preloader'>
			<div className='auth-call-back-preloader-header'>
				<NotificationUI.Notification />
			</div>
			<div className='auth-call-back-preloader-body'>
				{status === Status.Loading && (
					<div className='table-material-loader-wrapper'>
						<CircularProgress
							classes={{ colorPrimary: 'material-loader' }}
							color='primary'
							size={60}
						/>
					</div>
				)}
				{status === Status.Error && <BadAuthContainer />}
			</div>
		</div>
	)
}

async function processCode(code: string) {
	/*
	 #781 This is a stub, suppressEvents is a temp fix off app behavior, without it app flickers at switchToWorkspace
		exchangeAuthorizationCode fires snailySignedIn, switchToWorkspace fires appProfileChanged, it leads hat app is flickering
		It has to be fixed on app during #781, it should work when appProfileChanged is fired, there is no reason to show sign-in dialog for a second
		*/
	appConnector.eventHandlers.suppressEvents()
	try {
		const metadata = appConnector.utils.createMetadata()
		await appConnector.auth.exchangeAuthorizationCode(code, metadata)
		await updateLinkedIn(metadata)
	} finally {
		appConnector.eventHandlers.enableEvents()
	}
}

async function updateLinkedIn(metadata: any) {
	const linkedInProfiles = await appConnector.api.getLinkedInProfiles(metadata)
	// starting from here need to check is user Beta or follow one or another flow
	if (linkedInProfiles.length > 0) {
		const targetLinkedInProfile = linkedInProfiles[0]
		await appConnector.auth.switchToWorkspace(
			targetLinkedInProfile.id,
			metadata
		)
	}
}
